import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { RoundedBox } from '@react-three/drei';
import { sky } from '../render/skyState';
import { assetUrl } from '../services/assets';

// NeonSign — a venue's logo as a real neon sign on its front wall (2026-11-03).
//
// Khari: "the gangstafuel gym has its logo inside. I want to place a glowing neon
// version of that logo above the front door. it should glow in the evening." —
// and "make it slightly 3D (not flat) so it appears like an actual sign and not a
// label."
//
// Built the way neon signs are: the tube letters stand on a dark acrylic backer,
// which stands off the wall on four bolts.
//   THE BACKER   a rounded slab with depth, lit by the street like any surface.
//   THE TUBES    the logo's own shape, in LAYERS one behind the other (an alpha-
//                tested stack, ~2.5 cm of world depth), so from an angle the
//                letters have sides and catch the light — not a decal. The front
//                layer is the tube's face, paler (glass), the layers behind it
//                deeper green (its sides).
//   THE GLOW     dusk on (sky.night, the ramp CarLights uses): the tubes burn
//                (emissive, which the bloom pass catches), a soft halo of the
//                logo's own silhouette lights the backer, a wider one spills onto
//                the wall — and it flickers once as it comes on, like neon does.
//                By day it is unlit glass tubing on a black board.
// No real lights (the facades' rule: every point light costs every lit pixel).
// The logo is any image with transparency: it is cropped to its drawn part, so
// the sign fits the artwork, not the canvas around it.

const LAYERS = 6;            // the tubes' depth, front to back
const TUBE_DEPTH = 0.025;    // world units (≈5 cm at 2.2 m a unit)
const BACKER_D = 0.035;
const STANDOFF = 0.018;      // the gap between the wall and the backer
const PAD = 0.07;            // backer margin around the logo

/** The image's drawn part (alpha above a floor), as a canvas mask (white where drawn) + a blurred halo + its aspect. */
function useLogo(url) {
  const [art, setArt] = useState(null);
  useEffect(() => {
    let dead = false;
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => {
      if (dead) return;
      // Crop to what is drawn.
      const w = im.naturalWidth; const h = im.naturalHeight;
      const probe = document.createElement('canvas'); probe.width = w; probe.height = h;
      const pg = probe.getContext('2d', { willReadFrequently: true });
      pg.drawImage(im, 0, 0);
      const px = pg.getImageData(0, 0, w, h).data;
      let x0 = w; let y0 = h; let x1 = -1; let y1 = -1;
      for (let y = 0; y < h; y += 2) for (let x = 0; x < w; x += 2) {
        if (px[(y * w + x) * 4 + 3] > 24) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
      }
      if (x1 < 0) { x0 = 0; y0 = 0; x1 = w - 1; y1 = h - 1; }
      const cw = x1 - x0 + 1; const ch = y1 - y0 + 1;
      // THE MASK: white where the logo is drawn, so each layer's colour is the material's alone.
      const MW = 1024; const MH = Math.max(64, Math.round((MW * ch) / cw));
      const mask = document.createElement('canvas'); mask.width = MW; mask.height = MH;
      const mg = mask.getContext('2d');
      mg.drawImage(im, x0, y0, cw, ch, 0, 0, MW, MH);
      mg.globalCompositeOperation = 'source-in';
      mg.fillStyle = '#ffffff'; mg.fillRect(0, 0, MW, MH);
      // THE HALO: the same silhouette, blurred, with room around it. A shadow is
      // the one blur every browser draws (Safari's canvas `filter` is not), so the
      // logo is drawn off the canvas and its shadow is cast back onto it.
      const HW = 512; const HM = Math.round(HW * 0.18);
      const hh = Math.round(((HW - HM * 2) * ch) / cw) + HM * 2;
      const halo = document.createElement('canvas'); halo.width = HW; halo.height = hh;
      const hg = halo.getContext('2d');
      hg.shadowColor = '#ffffff'; hg.shadowBlur = 22; hg.shadowOffsetX = HW * 2;
      hg.drawImage(mask, -HW * 2 + HM, HM, HW - HM * 2, hh - HM * 2);
      const tex = (c) => { const t = new THREE.CanvasTexture(c); t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = 8; return t; };
      setArt({ mask: tex(mask), halo: tex(halo), aspect: cw / ch, haloScale: HW / (HW - HM * 2) });
    };
    im.onerror = () => { if (!dead) console.warn(`[NeonSign] could not load ${url}`); };
    im.src = assetUrl(url);
    return () => { dead = true; };
  }, [url]);
  useEffect(() => () => { art?.mask?.dispose(); art?.halo?.dispose(); }, [art]);
  return art;
}

/**
 * @param url    the logo (transparent PNG)
 * @param color  the neon's colour (the logo's own green for GangstaFuel)
 * @param width  the logo's width in world units (the backer is a margin wider)
 * Positioned by the caller with its BACK on the wall (z = 0) and its bottom at y = 0.
 */
export default function NeonSign({ url, color = '#74f84b', width = 2 }) {
  const art = useLogo(url);
  const glow = useMemo(() => new THREE.Color(color), [color]);
  const mats = useMemo(() => {
    const face = new THREE.MeshStandardMaterial({ color: glow.clone().lerp(new THREE.Color('#ffffff'), 0.28), emissive: glow.clone(), emissiveIntensity: 0.1, roughness: 0.25, metalness: 0, alphaTest: 0.5, side: THREE.DoubleSide });
    const sides = new THREE.MeshStandardMaterial({ color: glow.clone().multiplyScalar(0.45), emissive: glow.clone(), emissiveIntensity: 0.04, roughness: 0.4, metalness: 0, alphaTest: 0.5, side: THREE.DoubleSide });
    const halo = new THREE.MeshBasicMaterial({ color: glow.clone(), transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false });
    const spill = halo.clone();
    return { face, sides, halo, spill };
  }, [glow]);
  useEffect(() => {
    if (!art) return;
    mats.face.map = art.mask; mats.face.emissiveMap = art.mask;
    mats.sides.map = art.mask; mats.sides.emissiveMap = art.mask;
    mats.halo.map = art.halo; mats.spill.map = art.halo;
    for (const m of Object.values(mats)) m.needsUpdate = true;
  }, [art, mats]);
  useEffect(() => () => { for (const m of Object.values(mats)) m.dispose(); }, [mats]);

  // On at dusk, off at dawn — with one stutter as it strikes.
  const was = useRef(0);
  const strike = useRef(-1);
  useFrame((st) => {
    const on = THREE.MathUtils.smoothstep(sky.night, 0.12, 0.5);
    const t = st.clock.elapsedTime;
    if (on > 0.2 && was.current <= 0.2) strike.current = t;
    was.current = on;
    let k = on;
    const s = t - strike.current;
    if (strike.current >= 0 && s < 1.4) k *= [1, 0.15, 0.9, 0.05, 1, 0.4, 1][Math.min(6, Math.floor(s / 0.2))];
    // Bright but still GREEN: past ~1.6 the tone mapping washes the tubes toward white; the halo and the bloom carry the rest.
    mats.face.emissiveIntensity = 0.1 + k * 1.5;
    mats.sides.emissiveIntensity = 0.04 + k * 0.9;
    mats.halo.opacity = k * 0.8;
    mats.spill.opacity = k * 0.38;
    mats.halo.visible = k > 0.01; mats.spill.visible = k > 0.01;
  });

  if (!art) return null;
  const w = width; const h = width / art.aspect;
  const bw = w + PAD * 2; const bh = h + PAD * 2;
  const front = STANDOFF + BACKER_D;            // the backer's face
  const layers = Array.from({ length: LAYERS }, (_, i) => i);
  return (
    <group position={[0, bh / 2, 0]}>
      {/* The glow it throws on the wall around it. */}
      <mesh position={[0, 0, 0.004]} material={mats.spill} renderOrder={3} raycast={() => null}>
        <planeGeometry args={[w * art.haloScale * 1.5, h * art.haloScale * 1.9]} />
      </mesh>
      {/* Four bolts holding it off the wall. */}
      {[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([sx, sy]) => (
        <group key={`${sx}${sy}`} position={[sx * (bw / 2 - 0.05), sy * (bh / 2 - 0.05), 0]}>
          <mesh position={[0, 0, STANDOFF / 2]} rotation={[Math.PI / 2, 0, 0]} raycast={() => null}>
            <cylinderGeometry args={[0.012, 0.012, STANDOFF, 10]} />
            <meshStandardMaterial color="#8a8f94" metalness={0.8} roughness={0.35} />
          </mesh>
          <mesh position={[0, 0, front + 0.003]} rotation={[Math.PI / 2, 0, 0]} raycast={() => null}>
            <cylinderGeometry args={[0.016, 0.016, 0.006, 12]} />
            <meshStandardMaterial color="#b9bec2" metalness={0.9} roughness={0.25} />
          </mesh>
        </group>
      ))}
      {/* The backer: black acrylic with an edge you can see. */}
      <RoundedBox args={[bw, bh, BACKER_D]} radius={0.03} smoothness={3} position={[0, 0, STANDOFF + BACKER_D / 2]} raycast={() => null}>
        <meshStandardMaterial color="#0b0d0a" metalness={0.35} roughness={0.3} />
      </RoundedBox>
      {/* The glow on the backer, behind the tubes. */}
      <mesh position={[0, 0, front + 0.002]} material={mats.halo} renderOrder={3} raycast={() => null}>
        <planeGeometry args={[w * art.haloScale, h * art.haloScale]} />
      </mesh>
      {/* The tubes: the logo in layers, back (sides) to front (face). */}
      {layers.map((i) => (
        <mesh key={i} position={[0, 0, front + 0.006 + (TUBE_DEPTH * i) / (LAYERS - 1)]} material={i === LAYERS - 1 ? mats.face : mats.sides} raycast={() => null}>
          <planeGeometry args={[w, h]} />
        </mesh>
      ))}
    </group>
  );
}
